/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { useCallback, useEffect, useMemo, useState } from 'react';

import { useIntl } from 'react-intl';

import { cssMap, jsx } from '@atlaskit/css';
import StatusErrorIcon from '@atlaskit/icon/core/status-error';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Text } from '@atlaskit/primitives/compiled';
import { usePublish } from '@atlaskit/rovo-triggers/main';
import { token } from '@atlaskit/tokens';
import { logException } from '@atlassian/conversation-assistant-instrumentation/services/sentry';
import {
	JSM_CREATE_KB_ARTICLES_KEY,
	JSM_CREATE_KB_TOPICS_KEY,
} from '@atlassian/conversation-assistant-message-actions-api/constants';
import type { JsmCreateKbTopicsPayload } from '@atlassian/conversation-assistant-message-actions-api/jsm';
import type { ActionRenderer } from '@atlassian/conversation-assistant-message-actions-api/types';
import { useChatContextStoreActions } from '@atlassian/conversation-assistant-store/controllers/chat-context/store';
import {
	JSM_CREATE_KB_CLIENT_ID,
	JSM_CREATE_KB_STAGING_AREA_CLIENT_ID,
	JSM_KB_CARD_CLIENT_ID,
} from '@atlassian/rovo-content-bridge-api-commands/clients/jsm-create-kb-client';
import {
	JSMKbCardEventCommand,
	type KBCreateItemsData,
} from '@atlassian/rovo-content-bridge-api-commands/commands/jsm-create-kb-sync-commands';
import { useRovoContentBridgeAPI } from '@atlassian/rovo-content-bridge-api/hooks';

import { createKbApi } from './createKbApi';
import { CreateKBCardFooter } from './CreateKBCardFooter';
import { CreateKBCardForm, type KbTypeOption } from './CreateKBCardForm';
import { CreateKBCardHeader } from './CreateKBCardHeader';
import { CreateKBCardImagePreview } from './CreateKBCardImagePreview';
import { CreateKBCardPublishForm } from './CreateKBCardPublishForm';
import { createKbMessages } from './messages';
import { useCreateKbActivationId } from './useCreateKbActivationId';
import { useCreateKbCardViewState } from './useCreateKbCardViewState';
import { useCreateKbProjectIdentifier } from './useCreateKbProjectIdentifier';
import { useCreateKbPublishHandlers } from './useCreateKbPublishHandlers';
import { useCreateKbServerAppLinkId } from './useCreateKbServerAppLinkId';
import { useCreateKbSourceLinking } from './useCreateKbSourceLinking';
import { useCreateKbStagingAreaSync } from './useCreateKbStagingAreaSync';
import { usePrefetchedSpaceOptions } from './usePrefetchedSpaceOptions';

const styles = cssMap({
	cardContainer: {
		backgroundColor: token('elevation.surface.overlay', '#FFFFFF'),
		border: `${token('border.width')} solid ${token('color.border')}`,
		borderRadius: token('radius.xlarge', '16px'),
		boxShadow: token(
			'elevation.shadow.raised',
			'0 0 1px rgba(30, 31, 33, 0.31), 0 1px 1px rgba(30, 31, 33, 0.25)',
		),
		maxWidth: '400px',
		overflow: 'hidden',
	},
	errorRow: {
		display: 'flex',
		alignItems: 'flex-start',
		gap: token('space.050'),
		paddingTop: token('space.200'),
		paddingRight: token('space.250'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.250'),
	},
});

type CreateKbCardProps = ActionRenderer<JsmCreateKbTopicsPayload>;
const CREATE_KB_CARD_VIEWS = {
	IMAGE_PREVIEW: 'IMAGE_PREVIEW',
	CREATE_SPACE_FORM: 'CREATE_SPACE_FORM',
	PUBLISH_FORM: 'PUBLISH_FORM',
} as const;

export const CreateKBCard = ({
	actions,
	cloudId,
	aaid,
	isLastActionMessage,
}: CreateKbCardProps): JSX.Element => {
	const { formatMessage } = useIntl();
	const { setAIFeatureContext } = useChatContextStoreActions();
	const publish = usePublish('ai-mate');
	const data = actions?.[0]?.data as KBCreateItemsData;
	const projectId = data.projectId;
	const jsmActivationId = useCreateKbActivationId({ cloudId });
	const projectIdentifier = useCreateKbProjectIdentifier({ projectId });
	const appLinkId = useCreateKbServerAppLinkId({ cloudId, projectIdentifier });
	const { stagingAreaArticles, setupHandler } = useCreateKbStagingAreaSync({
		articleIds: data.articleIds ?? [],
		projectId,
		activationId: jsmActivationId ?? undefined,
		isLastActionMessage,
	});

	const key = actions?.[0]?.key;
	const isArticlesCard = key === JSM_CREATE_KB_ARTICLES_KEY;
	const initialType = useMemo<KbTypeOption>(
		() => ({ label: formatMessage(createKbMessages.typeInternalMessage), value: 'INTERNAL' }),
		[formatMessage],
	);
	const [selectedType, setSelectedType] = useState<KbTypeOption>(initialType);
	const [isCreateDraftsLoading, setIsCreateDraftsLoading] = useState(false);
	const [hasLinkSourceAlreadyHasContentError, setHasLinkSourceAlreadyHasContentError] =
		useState(false);
	const [hasGenericActionError, setHasGenericActionError] = useState(false);
	const [canCreateSpace, setCanCreateSpace] = useState(
		() => !appLinkId || !fg('jsm_kb_from_scratch_check_space_creation'),
	);
	const [isFetchingCreateSpacePermission, setIsFetchingCreateSpacePermission] = useState(
		() => Boolean(appLinkId) && fg('jsm_kb_from_scratch_check_space_creation'),
	);
	const { send, ready } = useRovoContentBridgeAPI(JSM_KB_CARD_CLIENT_ID, setupHandler);
	const { linkSpaceToKnowledgeBase } = useCreateKbSourceLinking();
	const { prefetchedSpaceOptions, isPrefetchedSpaceOptionsLoading } = usePrefetchedSpaceOptions({
		cloudId,
		userId: aaid,
	});

	useEffect(() => {
		ready();
	}, [ready]);

	useEffect(() => {
		return () => {
			setAIFeatureContext({
				contextKey: JSM_CREATE_KB_TOPICS_KEY,
				setContext: <T,>(_existingContext: T): T => undefined as T,
			});
			setAIFeatureContext({
				contextKey: JSM_CREATE_KB_ARTICLES_KEY,
				setContext: <T,>(_existingContext: T): T => undefined as T,
			});
		};
	}, [setAIFeatureContext]);

	useEffect(() => {
		if (!appLinkId || !fg('jsm_kb_from_scratch_check_space_creation')) {
			setCanCreateSpace(true);
			setIsFetchingCreateSpacePermission(false);
			return;
		}

		let isCancelled = false;
		const fetchCreateSpacePermission = async () => {
			setIsFetchingCreateSpacePermission(true);
			setCanCreateSpace(false);
			try {
				const hasCreateSpacePermission = await createKbApi.canCurrentConfluenceUserCreateSpace({
					cloudId,
				});
				if (!isCancelled) {
					setCanCreateSpace(hasCreateSpacePermission);
				}
			} catch {
				if (!isCancelled) {
					setCanCreateSpace(false);
				}
			} finally {
				if (!isCancelled) {
					setIsFetchingCreateSpacePermission(false);
				}
			}
		};

		void fetchCreateSpacePermission();
		return () => {
			isCancelled = true;
		};
	}, [appLinkId, cloudId]);

	useEffect(() => {
		if (!stagingAreaArticles.length) {
			return;
		}

		send(JSMKbCardEventCommand, {
			receiverId: JSM_CREATE_KB_STAGING_AREA_CLIENT_ID,
			senderId: JSM_KB_CARD_CLIENT_ID,
			type: 'ARTICLES',
			data: { articles: stagingAreaArticles },
		});
	}, [stagingAreaArticles, send]);

	const closeModal = useCallback(() => {
		send(JSMKbCardEventCommand, {
			data: { close: true },
			receiverId: JSM_CREATE_KB_CLIENT_ID,
			senderId: JSM_KB_CARD_CLIENT_ID,
			type: 'closeModal',
		});
	}, [send]);

	const { isConfirmLoading, handleConfirmCreateSpace, handlePublish } = useCreateKbPublishHandlers({
		cloudId,
		projectId,
		projectIdentifier,
		jiraActivationId: jsmActivationId ?? undefined,
		appLinkId,
		stagingAreaArticles,
		linkSpaceToKnowledgeBase,
		closeModal,
		onLinkSourceAlreadyHasContentErrorChange: setHasLinkSourceAlreadyHasContentError,
		onGenericPublishErrorChange: setHasGenericActionError,
	});

	const {
		cardView,
		spaceName,
		setSpaceName,
		selectedSpace,
		setSelectedSpace,
		publishNewSpaceName,
		isPublishConsentChecked,
		handlePublishConsentChange,
		handlePublishTargetChange,
		handleSaveForLater: handleSaveForLaterFromViewState,
		handlePublishAll: handlePublishAllFromViewState,
		handleBackToInitial,
		footerMode,
		isConfirmDisabled,
		isPublishDisabled,
	} = useCreateKbCardViewState({
		isArticlesCard,
		isConfirmLoading,
		projectIdentifier,
	});

	const handleCreateDrafts = useCallback(async () => {
		if (isCreateDraftsLoading) {
			return;
		}

		setIsCreateDraftsLoading(true);
		try {
			setHasGenericActionError(false);
			setHasLinkSourceAlreadyHasContentError(false);
			setAIFeatureContext({
				contextKey: JSM_CREATE_KB_TOPICS_KEY,
				setContext: <T,>(_existingContext: T): T => ({ articles: stagingAreaArticles }) as T,
			});

			await Promise.resolve(
				publish({
					type: 'message-send',
					source: 'create-kb-preview-card',
					data: {
						prompt: formatMessage(createKbMessages.ctaMessage),
						productKey: 'jsm',
					},
				}),
			);
		} catch (error) {
			setHasGenericActionError(true);
			logException({
				exception: error,
				name: 'CreateKbCardCreateDraftsError',
			});
		} finally {
			setIsCreateDraftsLoading(false);
		}
	}, [isCreateDraftsLoading, stagingAreaArticles, formatMessage, publish, setAIFeatureContext]);

	const handleBack = useCallback(() => {
		setHasGenericActionError(false);
		setHasLinkSourceAlreadyHasContentError(false);
		handleBackToInitial();
	}, [handleBackToInitial]);

	const handleSaveForLater = useCallback(() => {
		setHasGenericActionError(false);
		setHasLinkSourceAlreadyHasContentError(false);
		handleSaveForLaterFromViewState();
	}, [handleSaveForLaterFromViewState]);

	const handlePublishAll = useCallback(() => {
		setHasGenericActionError(false);
		setHasLinkSourceAlreadyHasContentError(false);
		handlePublishAllFromViewState();
	}, [handlePublishAllFromViewState]);

	return (
		<Box xcss={styles.cardContainer}>
			<CreateKBCardHeader />

			{cardView === CREATE_KB_CARD_VIEWS.CREATE_SPACE_FORM ? (
				<CreateKBCardForm spaceName={spaceName} onSpaceNameChange={setSpaceName} />
			) : cardView === CREATE_KB_CARD_VIEWS.PUBLISH_FORM ? (
				<CreateKBCardPublishForm
					selectedType={selectedType}
					onSelectedTypeChange={setSelectedType}
					selectedSpace={selectedSpace}
					appLinkId={appLinkId}
					prefetchedSpaceOptions={prefetchedSpaceOptions}
					isPrefetchedSpaceOptionsLoading={isPrefetchedSpaceOptionsLoading}
					onSelectedSpaceChange={setSelectedSpace}
					onPublishConsentChange={handlePublishConsentChange}
					onPublishTargetChange={handlePublishTargetChange}
					userId={aaid}
					cloudId={cloudId}
					canCreateSpace={canCreateSpace}
					isFetchingCreateSpacePermission={isFetchingCreateSpacePermission}
					hasLinkSourceAlreadyHasContentError={hasLinkSourceAlreadyHasContentError}
					hasGenericActionError={hasGenericActionError}
				/>
			) : (
				<CreateKBCardImagePreview />
			)}

			{hasGenericActionError && cardView !== CREATE_KB_CARD_VIEWS.PUBLISH_FORM ? (
				<Box xcss={styles.errorRow}>
					<StatusErrorIcon label="" color={token('color.icon.danger')} />
					<Text size="medium" color="color.text">
						{formatMessage(createKbMessages.genericActionErrorMessage)}
					</Text>
				</Box>
			) : null}

			<CreateKBCardFooter
				mode={footerMode}
				onCreateDrafts={handleCreateDrafts}
				onSaveForLater={handleSaveForLater}
				onPublishAll={handlePublishAll}
				onConfirm={() => handleConfirmCreateSpace({ spaceName, selectedTypeValue: 'EXTERNAL' })}
				onBack={handleBack}
				isCreateDraftsLoading={isCreateDraftsLoading}
				isConfirmLoading={isConfirmLoading}
				isPublishLoading={isConfirmLoading}
				isConfirmDisabled={isConfirmDisabled}
				isPublishDisabled={isPublishDisabled}
				onPublish={() =>
					handlePublish({
						publishNewSpaceName,
						isPublishToNewSpaceChecked: isPublishConsentChecked,
						selectedSpace,
						selectedTypeValue: selectedType.value,
					})
				}
			/>
		</Box>
	);
};
